import React, { useEffect, useState } from "react";
import { X, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

/**
 * AdBanner - renders admin-managed promotional banners.
 *  variant="public" → uses the no-auth /ads/public-banners (landing page).
 *  variant="app"    → uses the authed /ads/banners (inside the app, audience-aware).
 * Renders nothing when there are no active banners. Fully dismissible.
 */
export default function AdBanner({ variant = "app", isAr = false, className = "" }) {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState({});

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = variant === "public" ? await api.listPublicBanners() : await api.listBanners();
        if (alive) setItems((r?.items || []).filter(Boolean));
      } catch {
        if (alive) setItems([]);
      }
    })();
    return () => { alive = false; };
  }, [variant]);

  const visible = items.filter((b) => !dismissed[b.id]);
  if (!visible.length) return null;

  const onCta = (b) => {
    if (variant === "app") { api.trackBannerClick(b.id).catch(() => {}); }
    if (b.cta_url) {
      if (/^https?:\/\//.test(b.cta_url)) window.open(b.cta_url, "_blank", "noopener");
      else window.location.assign(b.cta_url);
    }
  };

  return (
    <div className={`space-y-2 ${className}`} data-testid="ad-banner-list">
      {visible.map((b) => {
        const img = b.image_base64
          ? (b.image_base64.startsWith("data:") ? b.image_base64 : `data:image/png;base64,${b.image_base64}`)
          : b.image_url || null;
        return (
          <div
            key={b.id}
            data-testid={`ad-banner-${b.id}`}
            className="relative flex items-center gap-3 overflow-hidden rounded-xl border border-primary/40 bg-gradient-to-r from-primary/10 via-card/70 to-card/40 p-3 pe-9 backdrop-blur"
          >
            {img && (
              <img src={img} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" loading="lazy" />
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-black text-foreground">{b.title}</p>
              {b.body && <p className="line-clamp-2 text-xs text-muted-foreground">{b.body}</p>}
            </div>
            {b.cta_url && (
              <button
                data-testid={`ad-banner-cta-${b.id}`}
                onClick={() => onCta(b)}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 text-xs font-bold text-primary-foreground hover:opacity-90"
              >
                {b.cta_label || (isAr ? "اعرف المزيد" : "Learn more")}
                <ExternalLink size={13} />
              </button>
            )}
            <button
              data-testid={`ad-banner-dismiss-${b.id}`}
              onClick={() => setDismissed((d) => ({ ...d, [b.id]: true }))}
              className="absolute end-2 top-2 grid h-6 w-6 place-items-center rounded-full text-muted-foreground hover:bg-white/10 hover:text-foreground"
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
